import React, { useId } from 'react';

/**
 * Shared label/helper/error semantics for the Input, Select and Textarea primitives.
 *
 * Why this exists: each primitive used to compute `aria-describedby` / `aria-invalid`
 * and then spread caller props over them, so a caller-supplied description silently
 * replaced the generated error association and `aria-invalid={false}` could contradict a
 * rendered error. The native control, its ref and every other caller prop are untouched;
 * only the two ARIA attributes are composed here, in one place.
 */

type AriaInvalid = React.AriaAttributes['aria-invalid'];

export interface FieldSemanticsInput {
  id?: string;
  error?: string;
  helperText?: string;
  /** Caller-supplied `aria-describedby` (space separated ID tokens). */
  'aria-describedby'?: string;
  /** Caller-supplied `aria-invalid` (boolean, 'true' | 'false' | 'grammar' | 'spelling'). */
  'aria-invalid'?: AriaInvalid;
}

export interface FieldSemantics {
  /** Explicit id when given, otherwise the stable `useId` value. */
  fieldId: string;
  errorId: string;
  helperId: string;
  /** Error text, only when it is non-empty (i.e. will be rendered). */
  error?: string;
  /** Helper text, only when it will be rendered (it yields to an error). */
  helperText?: string;
  /** Spread these AFTER the caller's remaining props so they cannot be overridden. */
  controlProps: {
    'aria-invalid': AriaInvalid;
    'aria-describedby': string | undefined;
  };
}

const splitTokens = (value: string | undefined): string[] =>
  value ? value.split(/\s+/).filter(Boolean) : [];

/**
 * Builds the final `aria-describedby`: the currently rendered message ID first (an error
 * must be announced before auxiliary hints), then caller IDs, de-duplicated. Caller tokens
 * that point at this field's own generated message IDs are only kept while that message is
 * actually rendered, so a state change never leaves a dangling reference.
 */
export function mergeDescribedBy(options: {
  renderedMessageId?: string;
  ownMessageIds: readonly string[];
  callerDescribedBy?: string;
}): string | undefined {
  const { renderedMessageId, ownMessageIds, callerDescribedBy } = options;
  const merged: string[] = [];
  const push = (token: string) => {
    if (!merged.includes(token)) merged.push(token);
  };
  if (renderedMessageId) push(renderedMessageId);
  for (const token of splitTokens(callerDescribedBy)) {
    if (ownMessageIds.includes(token) && token !== renderedMessageId) continue;
    push(token);
  }
  return merged.length > 0 ? merged.join(' ') : undefined;
}

/**
 * Precedence for `aria-invalid`:
 *  1. A rendered component error always wins and yields `true` (an explicit false/grammar/
 *     spelling cannot make a visibly erroring field look valid).
 *  2. With no component error, an explicit caller value is preserved as-is, including
 *     `true`, `'true'`, `'grammar'` and `'spelling'`.
 *  3. Otherwise `false`, as before.
 */
export function resolveAriaInvalid(hasError: boolean, explicit: AriaInvalid): AriaInvalid {
  if (hasError) return true;
  return explicit === undefined ? false : explicit;
}

export function useFieldSemantics({
  id,
  error,
  helperText,
  'aria-describedby': callerDescribedBy,
  'aria-invalid': callerInvalid,
}: FieldSemanticsInput): FieldSemantics {
  const reactId = useId();
  const fieldId = id || reactId;
  const errorId = `${fieldId}-error`;
  const helperId = `${fieldId}-helper`;
  const renderedError = error ? error : undefined;
  const renderedHelper = !renderedError && helperText ? helperText : undefined;

  return {
    fieldId,
    errorId,
    helperId,
    error: renderedError,
    helperText: renderedHelper,
    controlProps: {
      'aria-invalid': resolveAriaInvalid(Boolean(renderedError), callerInvalid),
      'aria-describedby': mergeDescribedBy({
        renderedMessageId: renderedError ? errorId : renderedHelper ? helperId : undefined,
        ownMessageIds: [errorId, helperId],
        callerDescribedBy,
      }),
    },
  };
}

export const FieldLabel: React.FC<{ htmlFor: string; children: React.ReactNode }> = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-400 mb-1">
    {children}
  </label>
);

/** Renders the single visible message (error, else helper) with the ID the control points at. */
export const FieldMessage: React.FC<{ field: FieldSemantics }> = ({ field }) => {
  if (field.error) {
    return (
      <p id={field.errorId} className="mt-1 text-sm text-red-400">
        {field.error}
      </p>
    );
  }
  if (field.helperText) {
    return (
      <p id={field.helperId} className="mt-1 text-sm text-slate-500">
        {field.helperText}
      </p>
    );
  }
  return null;
};
